import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { PenTool, BookOpen, Users, ShieldCheck, LogOut } from "lucide-react";
import ThemeToggle from "./ThemeToggle";

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-40 bg-stone-950/80 backdrop-blur-xl backdrop-saturate-150 border-b border-stone-800/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3" data-testid="navbar-logo">
          <PenTool className="w-4 h-4 text-rose-800" strokeWidth={1.5} />
          <span className="font-serif-heading text-xl tracking-wide text-stone-100">Inkwell</span>
          <span className="hidden md:inline font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 ml-2">
            Private Study
          </span>
        </Link>

        {user ? (
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="hidden md:inline font-sans-ui text-xs uppercase tracking-[0.2em] text-stone-400 hover:text-stone-100 transition px-3 py-2" data-testid="nav-stories">
              Stories
            </Link>
            <Link to="/characters" className="hidden md:inline font-sans-ui text-xs uppercase tracking-[0.2em] text-stone-400 hover:text-stone-100 transition px-3 py-2" data-testid="nav-characters">
              Characters
            </Link>
            {user.role === "admin" && (
              <Link to="/admin" className="hidden md:inline font-sans-ui text-xs uppercase tracking-[0.2em] text-rose-800 hover:text-rose-600 transition px-3 py-2" data-testid="nav-admin">
                Admin
              </Link>
            )}
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="nav-user-menu"
                  className="h-9 rounded-none border border-stone-800 bg-stone-900 hover:bg-stone-800 text-stone-200 font-sans-ui text-xs uppercase tracking-[0.18em] px-4">
                  {user.display_name || user.email}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-stone-950 border-stone-800 rounded-none w-52">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} className="font-sans-ui text-xs uppercase tracking-[0.15em] text-stone-400">
                  <BookOpen className="w-3.5 h-3.5 mr-2" /> My Stories
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/characters")} className="font-sans-ui text-xs uppercase tracking-[0.15em] text-stone-400">
                  <Users className="w-3.5 h-3.5 mr-2" /> Characters
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="font-sans-ui text-xs uppercase tracking-[0.15em] text-rose-700">
                    <ShieldCheck className="w-3.5 h-3.5 mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-stone-800" />
                <DropdownMenuItem onClick={onLogout} data-testid="nav-logout" className="font-sans-ui text-xs uppercase tracking-[0.15em] text-stone-400">
                  <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/auth" data-testid="nav-signin"
              className="font-sans-ui text-xs uppercase tracking-[0.2em] text-stone-400 hover:text-stone-100 px-4 py-2">
              Sign in
            </Link>
            <Link to="/auth?mode=register" data-testid="nav-register">
              <Button className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-9 px-5 font-sans-ui text-xs uppercase tracking-[0.2em]">
                Begin
              </Button>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
